import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { CvService } from '../cv.service';
import { GeneratedCv, Paginated } from '../../interfaces/cv';
import { Paged } from '../../interfaces/paged';
import { TreeNode } from '../../interfaces/treenode';
import { ColDef } from '../../interfaces/colDef';
import { TableOptions } from '../../settings/table-options/table-options.component';
import { SettingsService } from '../../shared/settings.service';
import { SubscribedComponent } from '../../subscribed.component';
import {
  DualAxisComponent,
  DualAxisDelegate,
  DualAxisEvent,
  Editable,
} from '../../dual-axis/dual-axis.component';
import { columns, stickyColumns, stickyColumnsRight } from './coldefs-cv';

/**
 * Paginated list of generated CVs, scoped to either a subject user or a
 * client. Backed by the bst-dual-axis component so the table inherits the
 * same sort/filter/search/column-show-hide chrome as projects, users,
 * clients and market vacancies.
 */
@Component({
  selector: 'bst-cv-list',
  templateUrl: './cv-list.component.html',
  styleUrls: ['./cv-list.component.scss'],
  standalone: false,
})
export class CvListComponent
  extends SubscribedComponent
  implements DualAxisDelegate<GeneratedCv>, OnChanges {

  @Input() public scope!: 'user' | 'client';
  @Input() public scopeId!: number;
  // Optional secondary filter: when the parent (client-cvs-tab) has a
  // picked subject user, restrict the list to that user's CVs without
  // narrowing the scope itself (scope stays 'client' so the user can
  // clear the picker and see all client CVs again).
  @Input() public subjectUserId: number | null = null;

  // DualAxisDelegate contract.
  public data: TreeNode<GeneratedCv>[] = [];
  public stickyColumnsLeft: ColDef[] = stickyColumns;
  public standardColumns: ColDef[] = columns;
  public stickyColumnsRight: ColDef[] = stickyColumnsRight;
  public events: Subject<DualAxisEvent> = new Subject<DualAxisEvent>();
  public count: number = 0;
  public options: TableOptions;

  // Row-level Editable hooks come for free with bst-dual-axis, but the CV
  // surface wants Preview + Download + Delete which aren't in the standard
  // view/edit/archive set. The custom `actions` column in coldefs-cv.ts
  // renders those buttons inside the cellTemplate instead.
  public editable?: Editable<GeneratedCv> = undefined;

  @ViewChild('cellTemplate', { static: true, read: TemplateRef })
  public cellTemplateOutlet!: TemplateRef<any>;

  @ViewChild('dualAxisComponent', { static: false })
  public dualAxis?: DualAxisComponent<GeneratedCv>;

  // Track deletes-in-flight per cv.id so the delete button can disable
  // itself without the cell template needing a separate map lookup helper.
  public deletingIds = new Set<number>();

  constructor(
    private readonly cvService: CvService,
    private readonly toastr: NbToastrService,
    private readonly translate: TranslateService,
    private readonly settings: SettingsService,
  ) {
    super();
    this.options = {
      sorts: [],
      // Column show/hide persists per user, same key family the other
      // overviews use (table_cv).
      display: this.settings.mergeTableColumns('table_cv', this.standardColumns),
      filter: {},
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['scope'] && !changes['scope'].firstChange) ||
      (changes['scopeId'] && !changes['scopeId'].firstChange) ||
      (changes['subjectUserId'] && !changes['subjectUserId'].firstChange)
    ) {
      this.data = [];
      if (this.dualAxis) {
        // void: dualAxis.reset() returns a promise; we don't need to await.
        void this.dualAxis.reset();
      }
    }
  }

  // --------------------------------------------------------------------
  // DualAxisDelegate.paged
  // --------------------------------------------------------------------

  public async paged(page: number): Promise<Paged<GeneratedCv>> {
    if (this.scope === undefined || this.scopeId === undefined || this.scopeId === null) {
      return null as any;
    }

    this.loading.next(true);
    try {
      const baseFilters = this.options.filter || {};
      const filters = this.subjectUserId
        ? { ...baseFilters, subject_user_id: this.subjectUserId }
        : baseFilters;
      const resp: Paginated<GeneratedCv> = await this.cvService.find({
        scope: this.scope,
        scopeId: this.scopeId,
        filters,
        sorts: this.options.sorts || [],
        page,
      }).toPromise();

      if (page <= 1) {
        this.data = [];
      }
      if (resp && resp.data) {
        this.count = resp.total;
        this.data = this.data.concat(resp.data.map(o => this.toNode(o)));
      }
      // Cast Paginated to Paged: backend always emits from/to even though
      // the narrow Paginated interface omits them.
      return resp as unknown as Paged<GeneratedCv>;
    } catch (err: unknown) {
      console.error('Failed to load CV list', err);
      this.toastr.danger(
        this.translate.instant('cv.errors.listFailed')
        || 'Could not load generated CVs.',
        this.translate.instant('cv.errors.title') || 'Error'
      );
      return null as any;
    } finally {
      this.loading.next(false);
    }
  }

  private toNode(cv: GeneratedCv): TreeNode<GeneratedCv> {
    return { data: cv, expanded: false, children: [] };
  }

  public openOptions(): void {
    if (this.dualAxis) {
      this.dualAxis.openOptions();
    }
  }

  // --------------------------------------------------------------------
  // Row actions
  // --------------------------------------------------------------------

  public onPreview(cv: GeneratedCv): void {
    // window.open returns null when popup blocker or enterprise policy
    // refuses the new tab. Surface that with a toast pointing at Download
    // as the always-available fallback rather than failing silently.
    const win = window.open(this.cvService.previewUrl(cv.id), '_blank', 'noopener');
    if (win === null) {
      this.toastr.warning(
        this.translate.instant('cv.errors.popupBlocked')
        || 'Your browser blocked the preview tab. Use Download instead.',
        this.translate.instant('cv.errors.title') || 'Preview blocked'
      );
    }
  }

  public onDownload(cv: GeneratedCv): void {
    const a = document.createElement('a');
    a.href = `/api/cv/${cv.id}/download`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  public onDelete(cv: GeneratedCv): void {
    if (this.deletingIds.has(cv.id)) return;

    const confirmMessage = this.translate.instant('cv.list.confirmDelete')
      || 'Delete this CV? This cannot be undone.';
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMessage)) return;

    this.deletingIds.add(cv.id);
    const sub = this.cvService.delete(cv.id).subscribe({
      next: () => {
        this.deletingIds.delete(cv.id);
        // Remove the deleted row from the in-memory tree so the table
        // updates without a full refetch.
        this.data = this.data.filter(node => node.data.id !== cv.id);
        this.count = Math.max(0, this.count - 1);
        this.toastr.success(
          this.translate.instant('cv.list.deleted') || 'CV deleted.',
          this.translate.instant('cv.list.title') || 'Generated CVs'
        );
      },
      error: (err: unknown) => {
        this.deletingIds.delete(cv.id);
        console.error('Failed to delete CV', err);
        this.toastr.danger(
          this.translate.instant('cv.errors.deleteFailed')
          || 'Could not delete this CV.',
          this.translate.instant('cv.errors.title') || 'Error'
        );
      },
    });
    this.subs.push(sub);
  }

  // --------------------------------------------------------------------
  // Cell-template helpers (consumed by the inline ng-template)
  // --------------------------------------------------------------------

  public isDeleting(cv: GeneratedCv): boolean {
    return this.deletingIds.has(cv.id);
  }

  public templateLabel(cv: GeneratedCv): string {
    const key = `cv.template_name.${cv.template_id}`;
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : cv.template_id;
  }

  public localeLabel(cv: GeneratedCv): string {
    const translated = this.translate.instant(cv.locale);
    return translated && translated !== cv.locale ? translated : cv.locale;
  }

  public formatSize(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined || isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`;
  }

  public daysUntilExpiry(cv: GeneratedCv): number | null {
    if (!cv.expires_at) return null;
    const expiry = new Date(cv.expires_at).getTime();
    if (isNaN(expiry)) return null;
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.ceil((expiry - Date.now()) / msPerDay);
  }

  public isExpiringSoon(cv: GeneratedCv): boolean {
    const days = this.daysUntilExpiry(cv);
    return days !== null && days >= 0 && days <= 7;
  }

  public expiryTooltip(cv: GeneratedCv): string {
    const days = this.daysUntilExpiry(cv);
    if (days === null) return '';
    const key = days <= 0 ? 'cv.list.expiryExpired' : 'cv.list.expirySoon';
    const fallback = days <= 0
      ? 'This CV is expired.'
      : `This CV expires in ${days} days`;
    const translated = this.translate.instant(key, { days });
    return translated && translated !== key ? translated : fallback;
  }
}
